export type Now = () => number;

export class VirtualClock {
  private anchorTime: number;
  private anchorNow: number;
  private playingState = false;
  private waitingState = false;
  private rateValue = 1;

  constructor(
    private readonly start: number,
    private readonly end: number,
    private readonly now: Now = () => performance.now(),
  ) {
    this.anchorTime = start;
    this.anchorNow = now();
  }

  get playing(): boolean {
    return this.playingState;
  }

  get rate(): number {
    return this.rateValue;
  }

  time(): number {
    if (!this.playingState || this.waitingState) return this.anchorTime;
    const time = this.anchorTime + (this.now() - this.anchorNow) * this.rateValue;
    if (time < this.end) return time;
    this.pauseAt(this.end);
    return this.end;
  }

  play(): void {
    if (this.playingState) return;
    if (this.anchorTime >= this.end) this.anchorTime = this.start;
    this.reanchor(() => (this.playingState = true));
  }

  pause(): void {
    this.reanchor(() => (this.playingState = false));
  }

  seek(time: number): void {
    this.anchorTime = Math.min(this.end, Math.max(this.start, time));
    this.anchorNow = this.now();
  }

  setRate(rate: number): void {
    this.reanchor(() => (this.rateValue = rate));
  }

  setWaiting(waiting: boolean): void {
    if (waiting !== this.waitingState) this.reanchor(() => (this.waitingState = waiting));
  }

  private pauseAt(time: number): void {
    this.anchorTime = time;
    this.anchorNow = this.now();
    this.playingState = false;
  }

  private reanchor(change: () => void): void {
    this.anchorTime = this.time();
    this.anchorNow = this.now();
    change();
  }
}
